import zod from 'zod';
import { SurgardIntegrationFormSchema } from '../../../services/IntegrationService/SurgardService/schemas';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import TextFieldComponent from '../../../components/TextFieldComponent';
import { Grid2 } from '@mui/material';
import ButtonComponent from '../../../components/ButtonComponent';
import CheckboxComponent from '../../../components/CheckboxComponent';
import { useContext, useEffect } from 'react';
import { AppContext } from '../../../context/AppContextProvider';
import {
  useCreateSurgardIntegrationAsync,
  useGetSurgardIntegrationByIdAsync,
  useUpdateSurgardIntegrationAsync,
} from '../../../services/IntegrationService/SurgardService/hooks/useMutation';
import DropdownComponent from '../../../components/DropdownComponent';
import { t } from 'i18next';

interface SurgardEditionFormProps {
  surgardIdToUpdate?: number;
  handleSaveForm?: () => void;
}

type SurgardEditionFormInput = zod.infer<typeof SurgardIntegrationFormSchema>;

const SurgardEditionForm = ({
  surgardIdToUpdate,
  handleSaveForm,
}: SurgardEditionFormProps) => {
  const { showSnackbar } = useContext(AppContext);
  const { control, handleSubmit, reset, getValues, watch } =
    useForm<SurgardEditionFormInput>({
      resolver: zodResolver(SurgardIntegrationFormSchema),
      defaultValues: {
        name: '',
        serverPort: '',
        serverIp: '',
        description: '',
        isActive: true,
        keepAliveInterval: 35000,
        type: 'SERVER',
        waitAck: true,
      },
    });

  const {
    mutate: mutateCreateSurgardIntegration,
    isLoading: isLoadingCreateSurgardIntegration,
  } = useCreateSurgardIntegrationAsync();
  const {
    mutate: mutateUpdateSurgardIntegration,
    isLoading: isLoadingUpdateSurgardIntegration,
  } = useUpdateSurgardIntegrationAsync();
  const {
    mutateAsync: mutateGetSurgardIntegrationByIdAsync,
    isLoading: isLoadingGetSurgardIntegrationByIdAsync,
  } = useGetSurgardIntegrationByIdAsync();

  watch('type');

  const handleSave = (data: SurgardEditionFormInput) => {
    if (surgardIdToUpdate) {
      mutateUpdateSurgardIntegration(
        { id: surgardIdToUpdate, data },
        {
          onSuccess: () => {
            saveIntegration('Integração surgard atualizada com sucesso.');
          },
        },
      );
      return;
    }
    mutateCreateSurgardIntegration(data, {
      onSuccess: () => {
        saveIntegration('Integração surgard criada com sucesso.');
      },
    });
  };

  const saveIntegration = (message: string) => {
    reset();
    showSnackbar(message, 'success');
    if (handleSaveForm) handleSaveForm();
  };

  useEffect(() => {
    if (surgardIdToUpdate) {
      mutateGetSurgardIntegrationByIdAsync(surgardIdToUpdate).then((r) =>
        reset(r, { keepDefaultValues: true }),
      );
    }
  }, [surgardIdToUpdate]);

  return (
    <>
      <Grid2 container columnSpacing={2}>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <DropdownComponent
                value={value}
                optionValue={'value'}
                optionLabel={'label'}
                options={[
                  { label: 'Cliente', value: 'CLIENT' },
                  { label: 'Servidor', value: 'SERVER' },
                ]}
                onChange={onChange}
                label={t('integrationsPage.surgardForms.typeConnection')}
              />
            )}
            name={'type'}
            control={control}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <TextFieldComponent
                value={value}
                errorMessage={error?.message}
                onChangeText={onChange}
                label={t('integrationsPage.surgardForms.name')}
              />
            )}
            name={'name'}
            control={control}
          />
        </Grid2>
        {getValues('type') !== 'SERVER' && (
          <Grid2 size={{ xs: 12, md: 6 }}>
            <Controller
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <TextFieldComponent
                  value={value}
                  errorMessage={error?.message}
                  onChangeText={onChange}
                  label={t('integrationsPage.surgardForms.ip')}
                />
              )}
              name={'serverIp'}
              control={control}
            />
          </Grid2>
        )}
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <TextFieldComponent
                value={value}
                errorMessage={error?.message}
                onChangeText={onChange}
                label={t('integrationsPage.surgardForms.door')}
              />
            )}
            name={'serverPort'}
            control={control}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <TextFieldComponent
                value={value}
                type="number"
                errorMessage={error?.message}
                onChangeText={(val) => onChange(Number(val))}
                label={t('integrationsPage.surgardForms.intervalKeepAlive')}
              />
            )}
            name={'keepAliveInterval'}
            control={control}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <TextFieldComponent
                value={value}
                errorMessage={error?.message}
                onChangeText={onChange}
                label={t('integrationsPage.surgardForms.description')}
              />
            )}
            name={'description'}
            control={control}
          />
        </Grid2>
        <Grid2 size={12} sx={{ display: 'flex', justifyContent: 'right' }}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <CheckboxComponent
                value={value}
                onChange={onChange}
                label={t('integrationsPage.surgardForms.waitACK')}
              />
            )}
            name={'waitAck'}
            control={control}
          />
        </Grid2>
        <Grid2 size={12} sx={{ display: 'flex', justifyContent: 'right' }}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <CheckboxComponent
                value={value}
                onChange={onChange}
                label={t('integrationsPage.surgardForms.active')}
              />
            )}
            name={'isActive'}
            control={control}
          />
        </Grid2>
        <Grid2 size={12} sx={{ mt: 2 }}>
          <ButtonComponent
            fullWidth
            onClick={handleSubmit(handleSave)}
            loading={
              isLoadingCreateSurgardIntegration ||
              isLoadingGetSurgardIntegrationByIdAsync ||
              isLoadingUpdateSurgardIntegration
            }
          >
            {t('integrationsPage.surgardForms.save')}
          </ButtonComponent>
        </Grid2>
      </Grid2>
    </>
  );
};

export default SurgardEditionForm;
